import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { Box, Typography } from '@mui/material';

interface ToastMessage {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info';
}

interface ToastContextType {
  showToast: (message: string, type?: 'success' | 'error' | 'info') => void;
}

const ToastContext = createContext<ToastContextType>({ showToast: () => {} });

export function useToast() {
  return useContext(ToastContext);
}

let nextId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const showToast = useCallback((message: string, type: 'success' | 'error' | 'info' = 'info') => {
    const id = nextId++;
    setToasts((prev) => [...prev, { id, message, type }]);
    const duration = type === 'info' ? 6000 : 4000;
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), duration);
  }, []);

  const colors = {
    success: { bg: '#dcfce7', border: '#86efac', color: '#15803d' },
    error: { bg: '#f5e8ea', border: '#c9a0a8', color: '#753742' },
    info: { bg: '#dceef1', border: '#61A0AF', color: '#003554' },
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {/* Toast container */}
      <Box sx={{ position: 'fixed', top: 16, right: 16, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 1, pointerEvents: 'none' }}>
        {toasts.map((t) => {
          const style = colors[t.type];
          return (
            <Box key={t.id} sx={{
              bgcolor: style.bg, border: `1.5px solid ${style.border}`, borderRadius: '8px',
              p: '10px 16px', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', pointerEvents: 'auto',
              animation: 'slideIn 0.3s ease-out', maxWidth: 420, display: 'flex', alignItems: 'center', gap: 1,
              '@keyframes slideIn': { from: { opacity: 0, transform: 'translateX(30px)' }, to: { opacity: 1, transform: 'translateX(0)' } },
            }}>
              <Typography sx={{ fontSize: '13px', color: style.color, fontWeight: 500, flex: 1 }}>{t.message}</Typography>
              <Box component="button" onClick={() => setToasts(prev => prev.filter(x => x.id !== t.id))} sx={{ p: 0, border: 'none', bgcolor: 'transparent', cursor: 'pointer', color: style.color, fontSize: '16px', lineHeight: 1, opacity: 0.6, flexShrink: 0, '&:hover': { opacity: 1 } }}>×</Box>
            </Box>
          );
        })}
      </Box>
    </ToastContext.Provider>
  );
}
